import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { Quote, ShieldCheck, Award } from 'lucide-react';

const Section = styled.section`
  padding: 100px 24px;
  background-color: #002147;
  color: white;
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ContentSide = styled.div``;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.1);
  padding: 8px 16px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 32px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #4ecdc4;
`;

const Title = styled.h2`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 24px;
  line-height: 1.2;
  color: #ffffff;
`;

const NoteText = styled.div`
  font-size: 18px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 32px;
  font-family: 'Georgia', serif;
  font-style: italic;
`;

const Signature = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Avatar = styled.div`
  width: 64px;
  height: 64px;
  background: #e2e8f0;
  border-radius: 50%;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AuthorInfo = styled.div``;

const AuthorName = styled.div`
  font-weight: 700;
  font-size: 18px;
  color: #ffffff;
`;

const AuthorTitle = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
`;

const OfferSide = styled(motion.div)`
  background: white;
  color: #0f172a;
  padding: 40px;
  border-radius: 24px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    border: 2px solid rgba(255, 255, 255, 0.1);
    border-radius: 32px;
    z-index: -1;
  }
`;

const OfferTitle = styled.h3`
  font-size: 24px;
  font-weight: 800;
  margin-bottom: 8px;
  color: #002147;
`;

const OfferPrice = styled.div`
  font-size: 48px;
  font-weight: 800;
  color: #002147;
  margin-bottom: 24px;
  display: flex;
  align-items: baseline;
  gap: 8px;

  span {
    font-size: 16px;
    color: #64748b;
    font-weight: 500;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 32px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  color: #334155;
`;

const Button = styled(motion.button)`
  width: 100%;
  padding: 18px;
  background: #002147;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 10px 20px rgba(0, 33, 71, 0.2);

  &:hover {
    background: #003366;
  }
`;

const EducationFoundersNote: React.FC = () => {
  const [pricing, setPricing] = useState({
    symbol: '$',
    price: 947,
  });

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const regionParam = urlParams.get('region');
        const apiUrl = regionParam ? `/api/pricing?region=${regionParam}` : '/api/pricing';

        const response = await fetch(apiUrl);
        if (response.ok) {
          const data = await response.json();
          if (data.plans && data.plans.lifetime) {
            setPricing({
              symbol: data.symbol,
              price: data.plans.lifetime.price
            });
          }
        }
      } catch (error) {
        console.log('Using default USD pricing');
      }
    };
    fetchPricing();
  }, []);

  return (
    <Section>
      <Container>
        <ContentSide>
          <Badge>
            <Quote size={16} /> A Note to Admissions Directors
          </Badge>
          <Title>
            Stop Losing Students to "Ghosting"
          </Title>
          <NoteText>
            "We built SmushChat because we saw too many brilliant students give up on their applications simply because they couldn't get a quick answer. In 2024, students expect instant gratification. If they have to wait until Monday for an answer, they've already moved on to another school. Our goal is to help you be there for them, 24/7, without burning out your staff."
          </NoteText>
          <Signature>
            <Avatar>
              {/* Placeholder for founder image */}
              <div style={{ width: '100%', height: '100%', background: '#cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontWeight: 700 }}>AF</div>
            </Avatar>
            <AuthorInfo>
              <AuthorName>Asif Farhan</AuthorName>
              <AuthorTitle>Founder, SmushChat</AuthorTitle>
            </AuthorInfo>
          </Signature>
        </ContentSide>

        <OfferSide
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
            <OfferTitle>Founders Edition</OfferTitle>
            <Award size={32} color="#fbbf24" />
          </div>

          <p style={{ color: '#64748b', marginBottom: '24px' }}>
            Lifetime access for early education partners.
          </p>

          <OfferPrice>
            {pricing.symbol}{pricing.price} <span>/ one-time</span>
          </OfferPrice>

          <FeatureList>
            <FeatureItem><ShieldCheck size={20} color="#16a34a" /> Lifetime License (No monthly fees)</FeatureItem>
            <FeatureItem><ShieldCheck size={20} color="#16a34a" /> 5 AI Agents</FeatureItem>
            <FeatureItem><ShieldCheck size={20} color="#16a34a" /> Priority Onboarding</FeatureItem>
            <FeatureItem><ShieldCheck size={20} color="#16a34a" /> Direct Founder Support</FeatureItem>
          </FeatureList>

          <Button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Claim Founders Edition
          </Button>

          <p style={{ textAlign: 'center', fontSize: '12px', color: '#94a3b8', marginTop: '16px' }}>
            Limited to first 50 institutions. 30-day money-back guarantee.
          </p>
        </OfferSide>
      </Container>
    </Section>
  );
};

export default EducationFoundersNote;
